import { StyledSelect } from "@ando/ui";
import * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { CheckCircle2, Key, Loader2, XCircle } from "lucide-react";

import { Badge, Button, cn, useToast } from "@ando/ui";
import type { AdminConfigureIdpRequest } from "@ando/core";

import {
  useAdminOrgIdp,
  useConfigureAdminOrgIdp,
  useRemoveAdminOrgIdp,
} from "@/features/admin/hooks/useAdminOrg";

import { ConfirmDialog, LoadingRow } from "./_shared";

/**
 * SSO tab — Identity Provider configuration (OIDC / SAML / Azure / Google).
 *
 * v1 reference: ``OrgSSOTab.tsx`` (270 LoC). The "Test connection" CTA is
 * intentionally absent — BE has no ``POST /idp/test`` endpoint as of
 * May 2026.
 */

export interface OrgSSOTabProps {
  orgId: string;
}

type IdpType = AdminConfigureIdpRequest["idp_type"];

function validateClientId(input: string): MessageDescriptor | null {
  if (!input.trim()) return msg`Client ID obbligatorio.`;
  if (input.length < 4) return msg`Client ID troppo corto.`;
  return null;
}

function validateIssuer(input: string): MessageDescriptor | null {
  if (!input.trim()) return msg`Issuer URL obbligatorio.`;
  try {
    const u = new URL(input);
    if (u.protocol !== "https:") return msg`L'issuer deve usare HTTPS.`;
  } catch {
    return msg`URL non valido.`;
  }
  return null;
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string | null;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
      >
        {label}
      </label>
      {children}
      {error ? (
        <span className="block text-[11px] text-destructive">{error}</span>
      ) : null}
    </div>
  );
}

export function OrgSSOTab({ orgId }: OrgSSOTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const idpQuery = useAdminOrgIdp(orgId);
  const configureIdp = useConfigureAdminOrgIdp(orgId);
  const removeIdp = useRemoveAdminOrgIdp(orgId);

  const [name, setName] = React.useState("");
  const [type, setType] = React.useState<IdpType>("OIDC");
  const [clientId, setClientId] = React.useState("");
  const [clientSecret, setClientSecret] = React.useState("");
  const [issuer, setIssuer] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = React.useState<string | null>(
    null,
  );

  const needsIssuer = type === "OIDC" || type === "SAML";
  const errors = touched
    ? {
        name: name.trim() ? null : msg`Nome obbligatorio.`,
        clientId: validateClientId(clientId),
        clientSecret: clientSecret.trim()
          ? null
          : msg`Client secret obbligatorio.`,
        issuer: needsIssuer ? validateIssuer(issuer) : null,
      }
    : { name: null, clientId: null, clientSecret: null, issuer: null };
  const hasErrors = Object.values(errors).some(Boolean);

  const submit = (): void => {
    setTouched(true);
    if (
      !name.trim() ||
      validateClientId(clientId) ||
      !clientSecret.trim() ||
      (needsIssuer && validateIssuer(issuer))
    ) {
      return;
    }
    configureIdp.mutate(
      {
        name: name.trim(),
        idp_type: type,
        client_id: clientId.trim(),
        client_secret: clientSecret,
        issuer: needsIssuer ? issuer.trim() : undefined,
      },
      {
        onSuccess: () => {
          toast.success(i18n._(msg`IdP configurato`), {
            description: i18n._(msg`${type} attivato.`),
          });
          setName("");
          setClientId("");
          setClientSecret("");
          setIssuer("");
          setTouched(false);
        },
        onError: (err) =>
          toast.error(i18n._(msg`IdP`), {
            description:
              err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          }),
      },
    );
  };

  const handleRemoveConfirmed = (): void => {
    if (!confirmRemoveId) return;
    const id = confirmRemoveId;
    removeIdp.mutate(id, {
      onSuccess: () => {
        toast.success(i18n._(msg`IdP rimosso`), {
          description: i18n._(msg`Configurazione SSO rimossa.`),
        });
        setConfirmRemoveId(null);
      },
      onError: (err) =>
        toast.error(i18n._(msg`IdP`), {
          description:
            err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  if (idpQuery.isLoading) {
    return <LoadingRow label={i18n._(msg`Caricamento configurazione SSO…`)} />;
  }

  const idps = idpQuery.data?.idps ?? [];

  return (
    <div className="flex flex-col gap-4">
      <section
        aria-labelledby="org-sso-existing"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-ando-orange-soft/40 text-accent">
            <Key size={16} aria-hidden />
          </div>
          <div>
            <h2
              id="org-sso-existing"
              className="font-semibold text-[13px] text-ink-1"
            >
              {i18n._(msg`Identity Provider (SSO)`)}
            </h2>
            <p className="text-[12px] text-ink-3">
              {i18n._(
                msg`Configura OIDC, SAML, Azure AD o Google SSO per questa org.`,
              )}
            </p>
          </div>
        </div>

        {idps.length > 0 ? (
          <ul className="mb-5 space-y-2">
            {idps.map((idp) => (
              <li
                key={idp.idp_id}
                className="flex items-center justify-between gap-3 rounded-lg border border-success/30 bg-success/10 px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2
                    size={15}
                    aria-hidden
                    className="text-success-foreground"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[12.5px] text-ink-1">
                        {idp.name || i18n._(msg`SSO Provider`)}
                      </p>
                      <Badge
                        variant={idp.source === "org" ? "accent" : "outline"}
                        className="text-[10px]"
                      >
                        {idp.source === "org"
                          ? i18n._(msg`Org`)
                          : i18n._(msg`Istanza`)}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-ink-3">
                      {i18n._(msg`Tipo: ${idp.type}`)}
                    </p>
                  </div>
                </div>
                {idp.source === "org" ? (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setConfirmRemoveId(idp.idp_id)}
                    aria-label={i18n._(msg`Rimuovi ${idp.name}`)}
                  >
                    <XCircle size={14} aria-hidden />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="max-w-lg space-y-3">
          {idps.length > 0 ? (
            <p className="font-medium text-[11.5px] text-ink-3">
              {i18n._(msg`Aggiungi un altro provider`)}
            </p>
          ) : null}
          <Field
            label={i18n._(msg`Nome provider`)}
            htmlFor="sso-name"
            error={errors.name ? i18n._(errors.name) : null}
          >
            <input
              id="sso-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={i18n._(msg`Google Workspace`)}
              className={cn(
                "h-8 w-full rounded-md border bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                errors.name
                  ? "border-destructive"
                  : "border-hairline focus-visible:border-accent",
              )}
            />
          </Field>
          <Field label={i18n._(msg`Tipo`)} htmlFor="sso-type">
            <StyledSelect
              id="sso-type"
              value={type}
              onChange={(e) => setType(e.target.value as IdpType)}
              className="h-8 w-full rounded-md border border-hairline bg-card px-2 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <option value="OIDC">OIDC (OpenID Connect)</option>
              <option value="SAML">SAML 2.0</option>
              <option value="AZURE_AD">Azure AD / Entra ID</option>
              <option value="GOOGLE">Google Workspace</option>
            </StyledSelect>
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={i18n._(msg`Client ID`)}
              htmlFor="sso-client-id"
              error={errors.clientId ? i18n._(errors.clientId) : null}
            >
              <input
                id="sso-client-id"
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder={i18n._(msg`your-client-id`)}
                className={cn(
                  "h-8 w-full rounded-md border bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  errors.clientId
                    ? "border-destructive"
                    : "border-hairline focus-visible:border-accent",
                )}
              />
            </Field>
            <Field
              label={i18n._(msg`Client secret`)}
              htmlFor="sso-client-secret"
              error={errors.clientSecret ? i18n._(errors.clientSecret) : null}
            >
              <input
                id="sso-client-secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                className={cn(
                  "h-8 w-full rounded-md border bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  errors.clientSecret
                    ? "border-destructive"
                    : "border-hairline focus-visible:border-accent",
                )}
              />
            </Field>
          </div>
          {needsIssuer ? (
            <Field
              label={i18n._(msg`Issuer URL`)}
              htmlFor="sso-issuer"
              error={errors.issuer ? i18n._(errors.issuer) : null}
            >
              <input
                id="sso-issuer"
                type="url"
                value={issuer}
                onChange={(e) => setIssuer(e.target.value)}
                placeholder="https://accounts.google.com"
                className={cn(
                  "h-8 w-full rounded-md border bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  errors.issuer
                    ? "border-destructive"
                    : "border-hairline focus-visible:border-accent",
                )}
              />
            </Field>
          ) : null}
          <Button
            type="button"
            onClick={submit}
            disabled={configureIdp.isPending || hasErrors}
            className="mt-1"
          >
            {configureIdp.isPending ? (
              <Loader2 size={13} aria-hidden className="mr-1.5 animate-spin" />
            ) : (
              <Key size={13} aria-hidden className="mr-1.5" />
            )}
            {i18n._(msg`Configura SSO`)}
          </Button>
        </div>
      </section>

      <ConfirmDialog
        open={confirmRemoveId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemoveId(null);
        }}
        title={i18n._(msg`Rimuovere configurazione IdP?`)}
        description={i18n._(
          msg`Gli utenti dell'organizzazione torneranno al login con email/password. Le sessioni attive non vengono interrotte.`,
        )}
        confirmLabel={
          removeIdp.isPending ? i18n._(msg`Rimozione…`) : i18n._(msg`Rimuovi`)
        }
        destructive
        loading={removeIdp.isPending}
        onConfirm={handleRemoveConfirmed}
      />
    </div>
  );
}
